/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useCallback, useEffect, useState } from "react";

//@Packages
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  EditIcon,
  Tab,
  Tabs,
  PageLoader,
  EmptyState,
} from "@forge/common";

//@Components
import OverviewTab from "./overview";
import DeleteModal from "../deletemodal";
import ActionModal from "../ActionModal";
import RemoteConnect from "../RemoteConnect";
import RevokeAuthModal from "../revokeauthmodal";
import Toast from "../../reusablecomponents/toast";
import LinkedDeviceList from "../linkedDeviceList";
import SoftwareDetails from "../SoftwareDetails";
import GatewayReport from "../gateway-report/GatewayReport";
import { ContextualMenuComponent } from "../../reusablecomponents/contextualmenu";
import NotificationBanner from "../../reusablecomponents/notificationbanner/notificationbanner";

//@Models
import { DeviceListProps } from "../../models/devicelist";

//@Actions
import { setHeaderDetails } from "../../actions";

//@Services
import {
  useGetDeviceDetailsQuery,
  useGetConnectedDeviceSoftwareVersionQuery,
  useGetAvailableSoftwareVersionsQuery,
} from "../../services/api";
import { useGetGatewayVersionDetailsApiQuery } from "../../services/file-service-api/fileServeApi";
import { useGetDownloadClientQuery } from "../../services/remote-connect-api";

//@Utils
import { useNavigateToLandingPage } from "../../utils/navigationUtils";
import {
  convertUTCStringtoFormattedDate,
  gatewayErrorStatusMessages,
  handleApiError,
  getRemoteAccess,
} from "../../utils/utility";

//@Constants
import {
  GatewayStatus,
  ROUTE_PATH,
  OVERVIEW_TAB_IDS,
  OVERVIEW_TAB_STRINGS,
  LOCALE_CONST,
  EBI_GATEWAY_TYPE,
  EBI_FORGE_COGNITION_GATEWAY_TYPE,
} from "../../utils/constants";

//@Config
import { ConfigurationVariables } from "../../config";
import { launchDarklyFeatureToggleKey } from "../../launchDarklyConfig";

//@Styles
import "./styles.scss";
import Software from "../Software";
import { IAvailableSoftwareVersion } from "../../models/softwaredetails";
import {
  mapConnectedDeviceSoftwareVersion,
  mapDisconnectedDeviceSoftwareVersions,
} from "../../utils/mapper";

//@Functional component
const ViewDevice = () => {
  const flags = useFlags();

  const NOT_FOUND_STATUS = 404;
  const UNAUTH_STATUS_CODE = 401;

  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname: locationId } = useLocation();
  const convertFn = (value: string) => t(value);
  const [deviceId, setDeviceId] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState("");
  const [reportTabTrigger, setReportTabTrigger] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [selectedRevokeItem, setSelectedRevokeItem] =
    useState<DeviceListProps | null>(null);
  const [changeTabs, setChangeTabs] = useState(OVERVIEW_TAB_IDS.OVERVIEW);
  const [toastType, setToastType] = useState<"success" | "error">("error");
  const [actionType, setActionType] = useState<"Activate" | "Deactivate">(
    "Deactivate",
  );
  const [gatewayVersionDetailsApiTrigger, setGatewayVersionDetailsApiTrigger] =
    useState(false);
  const [
    selectedAvailableSoftwareVersions,
    setSelectedAvailableSoftwareVersions,
  ] = useState<IAvailableSoftwareVersion[]>([]);

  useEffect(() => {
    setGatewayVersionDetailsApiTrigger(true);
  }, []);

  useEffect(() => {
    const splitLocIDs = locationId?.split?.("/");
    const gatewayId = splitLocIDs?.[splitLocIDs?.length - 1];

    setDeviceId(gatewayId || "");
  }, [locationId]);

  const {
    data,
    isError,
    isLoading,
    isSuccess,
    error: viewDeviceError,
    refetch: deviceDetailsRefetch,
  } = useGetDeviceDetailsQuery(
    { deviceId },
    { skip: !deviceId }, // Skip the query if deviceId is not available
  );

  useEffect(() => {
    dispatch(
      setHeaderDetails({
        headerDetails: {
          type: isSuccess ? "view" : "noView",
          title: data?.name || "",
          status: data?.status || "",
          healthStatus: data?.healthStatus || "",
          tag: data?.tag || data?.providerName || "",
        },
      }),
    );
  }, [data, isSuccess]);

  const {
    data: downloadClientData,
    error: downloadClientError,
    isLoading: downloadClientLoading,
  } = useGetDownloadClientQuery({}, { skip: !flags.enableBacsRemoteConnect });

  // Get connected device software version for EBI-Platform
  const isEBICognitionGateway = data?.gatewayType === EBI_FORGE_COGNITION_GATEWAY_TYPE;
  const isConnectedDevice = data?.connectivity === "Connected";

  const {
    data: availableSoftwareVersions,
    error: availableSoftwareVersionsError,
    isLoading: availableSoftwareVersionsLoading,
    refetch: availableSoftwareVersionsRefetch,
  } = useGetAvailableSoftwareVersionsQuery(
    {
      gatewayType: data?.gatewayType,
    },
    {
      skip: !data?.gatewayType || !isEBICognitionGateway || isConnectedDevice,
    },
  );

  const {
    data: connectedDeviceSoftwareVersion,
    error: connectedDeviceSoftwareVersionError,
    isLoading: connectedDeviceSoftwareVersionLoading,
    refetch: connectedDeviceSoftwareVersionRefetch,
  } = useGetConnectedDeviceSoftwareVersionQuery(
    {
      deviceId: data?.deviceId,
    },
    {
      skip: !data?.deviceId || !isEBICognitionGateway,
    },
  );

  useEffect(() => {
    if (availableSoftwareVersions !== undefined) {
      const mappedVersions = mapDisconnectedDeviceSoftwareVersions(
        availableSoftwareVersions,
      );
      setSelectedAvailableSoftwareVersions(mappedVersions);
    } else if (availableSoftwareVersionsError) {
      setSelectedAvailableSoftwareVersions([]);
    }
  }, [availableSoftwareVersions, availableSoftwareVersionsError]);

  useEffect(() => {
    if (isConnectedDevice) {
      if (connectedDeviceSoftwareVersion !== undefined) {
        const mappedVersions = mapConnectedDeviceSoftwareVersion(
          connectedDeviceSoftwareVersion,
        );
        setSelectedAvailableSoftwareVersions(mappedVersions);
      } else if (connectedDeviceSoftwareVersionError) {
        setSelectedAvailableSoftwareVersions([]);
      }
    }
  }, [connectedDeviceSoftwareVersion, connectedDeviceSoftwareVersionError, isConnectedDevice]);

  const effectiveInstalledSoftwareVersion =
    connectedDeviceSoftwareVersion?.installedVersion || data?.platformVersion || "";

  const {
    data: versionDetails,
    error: versionDetailsError,
    isError: versionDetailsIsError,
    isLoading: versionDetailsLoading,
    refetch: gatewayVersionDetailsRefetch,
  } = useGetGatewayVersionDetailsApiQuery(
    { systemGuid: deviceId },
    { skip: !deviceId || !gatewayVersionDetailsApiTrigger || !data?.gatewayType || isEBICognitionGateway },
  );

  const isGatewayDisconnected =
    data?.status === GatewayStatus.Error ||
    data?.status === GatewayStatus.Deactivated;

  useEffect(() => {
    if (changeTabs === OVERVIEW_TAB_IDS.OVERVIEW) {
      if (deviceId) {
        deviceDetailsRefetch();
        if (isEBICognitionGateway) {
          connectedDeviceSoftwareVersionRefetch();
          if (!isConnectedDevice) {
            availableSoftwareVersionsRefetch();
          }
        }

        if (data && gatewayVersionDetailsApiTrigger && data.gatewayType && !isEBICognitionGateway) {
          gatewayVersionDetailsRefetch();
        }
      }
    }
  }, [changeTabs, deviceId, gatewayVersionDetailsApiTrigger, isEBICognitionGateway, data]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const { navigateToLandingPage } = useNavigateToLandingPage();

  const onChangeTabs = (tabId: string) => {
    setChangeTabs(tabId);

    const triggers = {
      [OVERVIEW_TAB_IDS.OVERVIEW]: () =>
        setGatewayVersionDetailsApiTrigger(true),
      [OVERVIEW_TAB_IDS.REPORT]: () => setReportTabTrigger(true),
      default: () => {
        setGatewayVersionDetailsApiTrigger(false);
        setReportTabTrigger(false);
      },
    };

    (triggers[tabId] || triggers.default)();
  };

  //pass above data1 to validate with smple json
  const getRemoteAccessForEbi = getRemoteAccess(versionDetails?.details);
  //const disableRemoteConnectForEbi = data?.gatewayType === EBI_GATEWAY_TYPE;

  //function to render the tab view
  const renderTabView = useCallback(() => {
    const validStatus =
      data?.status === GatewayStatus.Active ||
      data?.status === GatewayStatus.Error;
    const validType =
      flags.gatewayTypeValidation?.showRemoteConnectTabLists?.includes(
        data?.gatewayType,
      );
    const disableReportTab =
      flags.gatewayTypeValidation?.disableReportTabLists?.includes(
        data?.gatewayType,
      );
    if (changeTabs === OVERVIEW_TAB_IDS.OVERVIEW) {
      //handle error codes
      if (viewDeviceError && "status" in viewDeviceError) {
        const convertFn = (value: string) => t(value);
        return (
          <EmptyState
            variant="4xx"
            title={
              typeof viewDeviceError.status === "number"
                ? gatewayErrorStatusMessages(
                  convertFn,
                  `${t(LOCALE_CONST.somethingWentWrong)}...`,
                  viewDeviceError.status,
                  { 404: t(LOCALE_CONST.clientError_404) },
                )
                : `${t(LOCALE_CONST.somethingWentWrong)}...`
            }
            description={`${t(LOCALE_CONST.tryReloading)}. (${t(LOCALE_CONST.errCode, { errorCode: viewDeviceError.status || t(LOCALE_CONST.error) })})`}
          >
            <Button className="mx-auto block" onClick={deviceDetailsRefetch}>
              {t(LOCALE_CONST.reloadPage)}
            </Button>
          </EmptyState>
        );
      } else {
        return (
          <OverviewTab
            data={data}
            versionDetails={versionDetails}
            isLoading={isLoading || versionDetailsLoading}
            gatewayType={data?.gatewayType}
            installedVersion={connectedDeviceSoftwareVersion?.installedVersion}
          />
        );
      }
    } else if (changeTabs === OVERVIEW_TAB_IDS.REPORT && !disableReportTab) {
      return (
        <GatewayReport
          triggerDiagnosticQuery={reportTabTrigger}
          gatewayType={data?.gatewayType}
        />
      );
    } else if (changeTabs === OVERVIEW_TAB_IDS.LINKED_DEVICES) {
      return <LinkedDeviceList gatewayType={data?.gatewayType} />;
    } else if (
      changeTabs === OVERVIEW_TAB_IDS.REMOTE_CONNECT &&
      validStatus &&
      validType
    ) {
      let hrefUrl = "";

      if (data?.gatewayType === EBI_GATEWAY_TYPE) {
        const generateEBIHrefUrl = () => {
          let hostRefValue = "";
          let EBI_FOLDER_USED_TO_REGISTER = "";

          if (getRemoteAccessForEbi?.properties?.name) {
            hostRefValue = getRemoteAccessForEbi?.properties?.name;
          }
          if (getRemoteAccessForEbi?.properties?.district) {
            EBI_FOLDER_USED_TO_REGISTER =
              getRemoteAccessForEbi?.properties?.district || "";
          }

          if (
            getRemoteAccessForEbi?.properties?.district &&
            getRemoteAccessForEbi?.properties?.name
          ) {
            return `${ConfigurationVariables.REMOTE_ACCESS_SECURE_CONNECT_URL}${EBI_FOLDER_USED_TO_REGISTER}/${hostRefValue}`;
          } else {
            return (
              `${ConfigurationVariables.REMOTE_ACCESS_SECURE_CONNECT_URL}` || ""
            );
          }
        };

        hrefUrl = generateEBIHrefUrl();
      } else {
        hrefUrl = data?.forgeRemoteUrl || "";
      }
      return (
        <RemoteConnect
          hrefUrl={hrefUrl}
          remoteAgentStatus={data?.remoteAgentStatus}
          enableBacsRemoteConnect={flags.enableBacsRemoteConnect}
          gatewayType={data?.gatewayType}
          disableRemoteConnectForEbi={true}
        />
      );
    } else if (changeTabs === OVERVIEW_TAB_IDS.SOFTWARE) {
      return <SoftwareDetails />;
    } else if (changeTabs === OVERVIEW_TAB_IDS.EBI_COGNITION_SOFTWARE) {
      return (
        <Software
          selectedAvailableSoftwareVersions={selectedAvailableSoftwareVersions}
          installedSoftwareVersion={effectiveInstalledSoftwareVersion}
          gatewayType={data?.gatewayType}
          deviceId={data?.deviceId}
          connectivity={data?.connectivity}
          isLoading={
            isConnectedDevice
              ? connectedDeviceSoftwareVersionLoading
              : availableSoftwareVersionsLoading
          }


        />
      );
    } else {
      return t(LOCALE_CONST.noPageFound);
    }
  }, [
    changeTabs,
    data,
    isLoading,
    versionDetails,
    versionDetailsLoading,
    reportTabTrigger,
    flags.gatewayTypeValidation,
    isConnectedDevice,
    connectedDeviceSoftwareVersionLoading,
    availableSoftwareVersionsLoading,
    selectedAvailableSoftwareVersions,
    effectiveInstalledSoftwareVersion,
    isEBICognitionGateway,
    connectedDeviceSoftwareVersion,
  ]);

  const handleActionClick = (type: "Activate" | "Deactivate") => {
    setActionType(type);
    setIsActionModalOpen(true);
  };

  const handleRevokeAuth = () => {
    setIsRevokeModalOpen(true);
    setSelectedRevokeItem(data);
  };

  const handleDownloadClient = async () => {
    try {
      const blob = await downloadClientData; // This will now be a Blob object
      const url = window.URL.createObjectURL(blob); // Create a URL for the Blob
      const link = document.createElement("a"); // Create a link element
      link.href = url;
      link.download = "forgeremoteclient.zip"; // Set the file name
      document.body.appendChild(link);
      link.click(); // Trigger the download
      document.body.removeChild(link); // Clean up the link element
      setToastType("success");
      setToastContent(t(LOCALE_CONST.downloadClientSuccess));
      setShowToast(true);
    } catch (error) {
      setToastType("error");
      const errorMessage = handleApiError(
        error,
        convertFn,
        t(LOCALE_CONST.notFound_dc_404),
        {
          401: t(LOCALE_CONST.unauthorized_dc_401),
          403: t(LOCALE_CONST.forbidden_dc_403),
          404: t(LOCALE_CONST.notFound_dc_404),
          500: t(LOCALE_CONST.internalServerError_dc_500),
        },
      );
      setToastContent(errorMessage);
      setShowToast(true);
    }
  };

  const refetchDeviceList = () => navigateToLandingPage();

  const getMenuItems = (status: string) => {

    if (data?.connectivity === "Disconnected") {
      return [
        {
          id: `delete`,
          label: t(LOCALE_CONST.delete),
          onClick: () => setIsDeleteModalOpen(true),
        }
      ];
    } else if (status === GatewayStatus.Pending) {
      return [
        {
          id: `deactivate`,
          label: t(LOCALE_CONST.deactivate),
          onClick: () => handleActionClick("Deactivate"),
        }
      ];
    } else if (
      status === GatewayStatus.Active ||
      status === GatewayStatus.Error
    ) {
      return [
        {
          id: `deactivate`,
          label: t(LOCALE_CONST.deactivate),
          onClick: () => handleActionClick("Deactivate"),
        },
        {
          id: `revokeauth`,
          label: t(LOCALE_CONST.revokeAuth),
          onClick: () => handleRevokeAuth(),
        }
      ];
    } else if (status === GatewayStatus.Deactivated) {
      return [
        {
          id: `activate`,
          label: t(LOCALE_CONST.activate),
          onClick: () => handleActionClick("Activate"),
        },
        {
          id: `delete`,
          label: t(LOCALE_CONST.delete),
          onClick: () => setIsDeleteModalOpen(true),
        },
      ];
    } else {
      return [];
    }
  };

  const renderEditContainer = () => {
    const disableActionMenuForLegacy =
      (data?.providerName === "legacy" ||
        data?.providerName === "forgelegacy") &&
      flags.isLegacyGateway?.actionItemsDisabled === "True";
    if (changeTabs !== OVERVIEW_TAB_IDS.REMOTE_CONNECT) {
      const renderEditBlock = () => {
        const validEditStatus =
          data?.status === GatewayStatus.Deactivated ||
          data?.status === GatewayStatus.Active ||
          data?.status === GatewayStatus.Error;
        const isCertificateExpired =
          data?.status === GatewayStatus.CertificateExpired ||
          data?.status === GatewayStatus.Pending;
        if (changeTabs === OVERVIEW_TAB_IDS.OVERVIEW) {
          return (
            <Button
              icon={<EditIcon strokeWidth={2} />}
              disabled={
                isError ||
                changeTabs !== OVERVIEW_TAB_IDS.OVERVIEW ||
                !validEditStatus
              }
              onClick={() =>
                navigate(
                  `${isCertificateExpired ? ROUTE_PATH.reRegistration : ROUTE_PATH.editDevice}/${data?.deviceId}`,
                )
              }
            >
              {t(LOCALE_CONST.edit)}
            </Button>
          );
        } else {
          return null;
        }
      };

      const isActionMenuDisabled = (status: string) => {
        if (
          status === GatewayStatus.Activating ||
          status === GatewayStatus.Deactivating ||
          status === GatewayStatus.Deleting ||
          status === GatewayStatus.CertificateExpired
        ) {
          return true;
        } else {
          return false;
        }
      };

      return (
        <div className="view-device-right-container">
          <div className="view-device-connect-button">{renderEditBlock()}</div>
          <div className="view-device-contextual-menu ml-4">
            <ContextualMenuComponent
              provider={data?.providerName}
              menuItems={getMenuItems(data?.status)}
              disabled={
                isError ||
                isActionMenuDisabled(data?.status) ||
                disableActionMenuForLegacy
              }
            />
          </div>
        </div>
      );
    } else if (changeTabs === OVERVIEW_TAB_IDS.REMOTE_CONNECT) {
      return (
        <>
          {flags.enableBacsRemoteConnect && (
            <div className="view-device-right-container">
              <Button variant="secondary" onClick={handleDownloadClient}>
                {t(LOCALE_CONST.downloadClient)}
              </Button>
            </div>
          )}
        </>
      );
    } else {
      return "";
    }
  };

  const renderDeviceTabs = useCallback(() => {
    const validStatus =
      data?.status === GatewayStatus.Active ||
      data?.status === GatewayStatus.Error;
    const validType =
      flags.gatewayTypeValidation?.showRemoteConnectTabLists?.includes(
        data?.gatewayType,
      );
    const disableReportTab =
      flags.gatewayTypeValidation?.disableReportTabLists?.includes(
        data?.gatewayType,
      );
    const disableReportTabForLegacy =
      (data?.providerName === "legacy" ||
        data?.providerName === "forgelegacy") &&
      flags.isLegacyGateway?.viewDevice?.includes("Report");
    const disableLinkedDeviceTabForLegacy =
      (data?.providerName === "legacy" ||
        data?.providerName === "forgelegacy") &&
      flags.isLegacyGateway?.viewDevice?.includes("LinkedDevice");
    const disableRemoteConnectTabForLegacy =
      (data?.providerName === "legacy" ||
        data?.providerName === "forgelegacy") &&
      flags.isLegacyGateway?.viewDevice?.includes("RemoteConnect");
    const enableSoftwareTabForLegacy =
      (data?.providerName?.toLowerCase() === "forgelegacy" ||
        data?.tag?.toLowerCase() === "forgelegacy") &&
      flags[launchDarklyFeatureToggleKey.ENABLE_FC_SOFTWARE_DETAILS];
    const enableEbiCognitionTab =
      data?.gatewayType === EBI_FORGE_COGNITION_GATEWAY_TYPE &&
      flags.enableEbiCognition;
    const isEBIGatewayConnected = data?.connectivity === "Connected";
    // OnGuard Gateway feature flag logic
    const isOnGuardGateway = data?.gatewayType === "OnGuard";
    let lists: string[] = [];

    if (enableEbiCognitionTab) {
      lists = ["OVERVIEW", "EBI_COGNITION_SOFTWARE"];
      if (isEBIGatewayConnected) {
        lists.push("REMOTE_CONNECT");
      }
    }
    else if (isOnGuardGateway) {
      lists = ["OVERVIEW"];
    }
    else {
      lists = ["OVERVIEW", "REPORT", "LINKED_DEVICES"];

      if (enableSoftwareTabForLegacy) {
        lists.push("SOFTWARE");
      }
      if (validStatus && validType) {
        lists.push("REMOTE_CONNECT");
      }

    }

    return lists.map((item: string, index: number) => {
      let propObj = { id: OVERVIEW_TAB_IDS[item], disabled: false };

      if (
        item === "REPORT" &&
        (isGatewayDisconnected || disableReportTab || disableReportTabForLegacy)
      ) {
        propObj = { ...propObj, disabled: true };
      }

      if (
        item === "LINKED_DEVICES" &&
        (isGatewayDisconnected || disableLinkedDeviceTabForLegacy)
      ) {
        propObj = { ...propObj, disabled: true };
      }

      if (item === "REMOTE_CONNECT" && disableRemoteConnectTabForLegacy) {
        propObj = { ...propObj, disabled: true };
      }
      // Dynamically update the tab name for REMOTE_CONNECT based on enableBacsRemoteConnect
      const tabName =
        item === "REMOTE_CONNECT" && flags.enableBacsRemoteConnect
          ? t(LOCALE_CONST.remoteConnect) // Use "Connect" if enabled
          : t(OVERVIEW_TAB_STRINGS[item]); // Default tab name

      return (
        <Tab key={index} {...propObj}>
          {tabName}
        </Tab>
      );
    });
  }, [
    data,
    isGatewayDisconnected,
    flags.gatewayTypeValidation,
    flags.enableBacsRemoteConnect,
    flags.isLegacyGateway,
    flags.enableEbiCognition,
    flags.enableFcSoftwareDetails,
  ]);

  const valType = () => {
    if (toastType === "success") {
      return t(LOCALE_CONST.success);
    } else {
      return t(LOCALE_CONST.error);
    }
  };

  const errorDescription = (val: DeviceListProps) => {
    let error = t(LOCALE_CONST.gwConnectionFailed);

    if (val?.lastHeartBeatTimeStamp) {
      error = `${t(LOCALE_CONST.gwConnectionFailedAt)} ${convertUTCStringtoFormattedDate(val?.lastHeartBeatTimeStamp)}`;
    }

    return error;
  };

  const errorStatusValue = () => {
    // Example: safely access status or message depending on error type
    if (versionDetailsError && "originalStatus" in versionDetailsError) {
      return versionDetailsError.originalStatus;
    }

    if (versionDetailsError && "status" in versionDetailsError) {
      return versionDetailsError.status;
    }

    return undefined;
  };

  const renderVersionWarnCompFn = () => {
    if (
      changeTabs === OVERVIEW_TAB_IDS.OVERVIEW &&
      (errorStatusValue() === NOT_FOUND_STATUS ||
        errorStatusValue() === UNAUTH_STATUS_CODE)
    ) {
      return (
        <div className="view-device-notification-banner">
          <NotificationBanner
            variant="inline"
            type="warning"
            title={t(LOCALE_CONST.warning)}
            description={t(LOCALE_CONST.nameplateNotConfigured)}
            canClose={false}
          />
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <>
      <PageLoader isLoading={isLoading || versionDetailsLoading}></PageLoader>
      {data?.status === GatewayStatus.Error && (
        <div className="view-device-notification-banner">
          <NotificationBanner
            variant="inline"
            type="critical"
            title={t(LOCALE_CONST.error)}
            description={errorDescription(data)}
            canClose={false}
          />
        </div>
      )}

      {!isEBICognitionGateway && renderVersionWarnCompFn()}

      <div className="iot-view-device-tabs-container-cls">
        <Tabs
          iconPosition="left"
          value={changeTabs}
          onChange={(tabId) => onChangeTabs(tabId)}
        >
          {renderDeviceTabs()}
        </Tabs>
        {renderEditContainer()}
      </div>
      <div>
        {renderTabView()}

        {showToast && (
          <div className="toast-container">
            <div
              onClick={() => setShowToast(false)}
              style={{ cursor: "pointer" }}
            >
              <Toast
                id="gateway-version-toast"
                type={toastType}
                content=""
                showClose={true}
                title={valType()}
                subtitle={toastContent}
                showDismiss={false}
              />
            </div>
          </div>
        )}
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          deviceName={data?.name || ""}
          deviceId={data?.deviceId || ""}
          refetchDeviceList={refetchDeviceList}
        />
        <ActionModal
          isOpen={isActionModalOpen}
          onClose={() => setIsActionModalOpen(false)}
          actionType={actionType}
          deviceName={data?.name || ""}
          deviceId={data?.deviceId || ""}
          refetchDeviceList={refetchDeviceList}
        />
        <RevokeAuthModal
          isOpen={isRevokeModalOpen}
          deviceId={selectedRevokeItem?.deviceId || ""}
          onClose={() => setIsRevokeModalOpen(false)}
          deviceName={selectedRevokeItem?.name || ""}
          refetchDeviceList={refetchDeviceList}
        />
      </div>
    </>
  );
};

export default ViewDevice;
